<script>
    /**
     * To customize `SiteHeader`'s colors, you may set any of the following CSS
     * variables on a parent of the component (for example, you can set these
     * CSS variables on `<body>`; see Additional tests/Site Header` for
     * examples).
     *
     * - `--site-header-color-text`: text color in the header (including the
     *     wordmark)
     * - `--site-header-fixed-color-text`: text color in the fixed header that
     *    appears when you scroll the page (including the wordmark)
     * - `--site-header-color-bg`: background color for the header (before
     *     scroll)
     * - `--site-header-fixed-color-bg`: background color for the fixed header
     *     that appears when you scroll the page
     * - `--site-header-color-button-text`: text/icon color for the menu button
     *     (before scrolling)
     * - `--site-header-fixed-color-button-text`: text/icon color  for the menu
     *     button on the fixed header that appears when you scroll the page
     * - `--site-header-color-button-bg`: background color for the menu button
     * - `--site-header-color-border`: color for the border at the bottom of the
     *     header (before scrolling)
     * - `--site-header-fixed-color-border`: color for the border at the bottom
     *     of the fixed header that appears when you scroll the page
     *
     * Note that the text and background colors of the overlay are not
     * customizable.
     *
     * Setting any of these CSS variables is __optional__; in most cases, there
     * is no need to set them. If you do set any of these CSS variables on your
     * page, make sure to consider if the value works in both light and dark
     * mode (using an
     * [adapative color variable](https://guides.propublica.org/design/storybook/?path=/story/pp-v5-shared_config-colors--page#adaptive-color-variables),
     * like `--color-accent-30`, is one way to handle this).
     *
     * @component
     */

    import { onMount } from "svelte";

    import MenuIcon from "@propublica/pp-v5-shared/src/icons/menu.svg";
    import SearchIcon from "@propublica/pp-v5-shared/src/icons/search.svg";
    import Logo from "@propublica/pp-v5-shared/src/icons/propublica-logo.svg";
    // Modal
    import CloseIcon from "@propublica/pp-v5-shared/src/icons/close.svg";
    import FacebookIcon from "@propublica/pp-v5-shared/src/icons/social-facebook.svg";
    import InstagramIcon from "@propublica/pp-v5-shared/src/icons/social-instagram.svg";
    import TwitterIcon from "@propublica/pp-v5-shared/src/icons/social-x.svg";
    // Topic Icons
    import TopicRacialJusticeIcon from "@propublica/pp-v5-shared/src/icons/topic-racial-justice.svg";
    import TopicHealthcareIcon from "@propublica/pp-v5-shared/src/icons/topic-healthcare.svg";
    import TopicPoliticsIcon from "@propublica/pp-v5-shared/src/icons/topic-politics.svg";
    import TopicCriminalJusticeIcon from "@propublica/pp-v5-shared/src/icons/topic-criminal-justice.svg";
    import NewsletterSignup from "../NewsletterSignup";
    import Button from "../Button";

    /** How much of the site nav to show. Use "minimal" over full-bleed content.
     * @type {( 'full' | 'compact' | 'minimal' )}
     */
    export let type = "full";

    /**
     * When the Fixed nav should appear. Accepts a querySelector string and
     * calculates its position and height on the page.
     * @type { string }
     */
    export let fixedNavScrollStart = "";

    /**
     * Optional props to pass through to the `NewsletterSignup` component. You
     * can pass an object with any of the following keys:
     *
     * - `source`
     * - `region`
     *
     * See `NewsletterSignup` for details on allowed values.
     *
     * Any keys that you omit fall back to `NewsletterSignup`'s default values
     * for these props, which are generally the right ones for news apps.
     *
     * @type {HeaderFooterNewsletterProps}
     */
    export let newsletterSignupProps = {};

    /**
     * URL to use for donate link in header and overlay. If not specified,
     * defaults to URL with preferred tracking param for www.
     * @type {string}
     */
    export let donateUrl =
        "https://give.propublica.org/give/346423/#!/donation/checkout?c_src=UpRed";

    // These variables should get bound as soon as component renders
    /** @type {HTMLElement} */
    let scrollStartSiteHeaderEl;
    /** @type {HTMLInputElement} */
    let overlaySearchInputEl;

    let scrollY = 0;
    let isOverlayOpen = false;
    let fixedNavScrollStartY = 55;

    let breakpoint = "xs";

    onMount(() => {
        function setBreakpoint() {
            if (window.matchMedia("(min-width: 0)").matches) {
                breakpoint = "xs";
            }
            if (window.matchMedia("(min-width: 36em)").matches) {
                breakpoint = "sm";
            }
            if (window.matchMedia("(min-width: 48em)").matches) {
                breakpoint = "md";
            }
            if (window.matchMedia("(min-width: 60em)").matches) {
                breakpoint = "lg";
            }
            if (window.matchMedia("(min-width: 78em)").matches) {
                breakpoint = "xl";
            }
            if (window.matchMedia("(min-width: 88em)").matches) {
                breakpoint = "xxl";
            }
        }
        window.addEventListener("resize", setBreakpoint);
        setBreakpoint();

        // Update fixedNavScrollStartY to be the height of SiteHeader or fixedNavScrollStart element.
        function updateFixedNavStart() {
            /** @type {HTMLElement} */
            let el;
            try {
                el =
                    document.querySelector(fixedNavScrollStart) ??
                    scrollStartSiteHeaderEl;
            } catch {
                el = scrollStartSiteHeaderEl;
            }
            const top = el.getBoundingClientRect().top + window.pageYOffset;
            const height = el.offsetHeight;
            fixedNavScrollStartY = top + height;
        }
        window.addEventListener("resize", updateFixedNavStart);
        updateFixedNavStart();
    });

    function openOverlay() {
        isOverlayOpen = true;
    }

    function closeOverlay() {
        isOverlayOpen = false;
    }

    /**
     * @param {KeyboardEvent} event
     */
    function handleKeydown(event) {
        if (event.key === "Escape") {
            if (isOverlayOpen === true) {
                closeOverlay();
            }
        }
    }

    /**
     * @typedef {object} HeaderLink
     * @property {string} text
     * @property {string} href
     */
    /**
     * @typedef {HeaderLink & {icon?: any} } OverlayLink
     */
    /**
     * @typedef {object} OverlayLinkSection
     * @property {string} heading
     * @property {string} classSuffix
     * @property {OverlayLink[]} links
     */

    /** @type {OverlayLinkSection[]} */
    const overlayLinkSections = [
        {
            heading: "Topics",
            classSuffix: "topics",
            links: [
                {
                    text: "Racial Justice",
                    icon: TopicRacialJusticeIcon,
                    href: "https://www.propublica.org/topics/racial-justice",
                },
                {
                    text: "Health Care",
                    icon: TopicHealthcareIcon,
                    href: "https://www.propublica.org/topics/health-care",
                },
                {
                    text: "Politics",
                    icon: TopicPoliticsIcon,
                    href: "https://www.propublica.org/topics/politics",
                },
                {
                    text: "Criminal Justice",
                    icon: TopicCriminalJusticeIcon,
                    href: "https://www.propublica.org/topics/criminal-justice",
                },
                {
                    text: "more…",
                    href: "https://www.propublica.org/topics/",
                },
            ],
        },
        {
            heading: "Browse by Place",
            classSuffix: "region",
            links: [
                {
                    text: "Midwest",
                    href: "https://www.propublica.org/midwest/",
                },
                {
                    text: "Northwest",
                    href: "https://www.propublica.org/northwest/",
                },
                { text: "South", href: "https://www.propublica.org/south/" },
                {
                    text: "Southwest",
                    href: "https://www.propublica.org/southwest/",
                },
                { text: "Texas", href: "https://www.propublica.org/texas/" },
            ],
        },
        {
            heading: "Type",
            classSuffix: "type",
            links: [
                {
                    text: "Graphics & Data",
                    href: "https://www.propublica.org/newsapps/",
                },
                {
                    text: "Newsletters",
                    href: "https://www.propublica.org/newsletters/",
                },
                { text: "Series", href: "https://www.propublica.org/series/" },
                { text: "Videos", href: "https://www.propublica.org/video/" },
            ],
        },
        {
            heading: "",
            classSuffix: "special",
            links: [
                {
                    text: "Local Reporting Network",
                    href: "https://www.propublica.org/local-reporting-network/",
                },
                {
                    text: "Electionland",
                    href: "https://www.propublica.org/electionland/",
                },
                {
                    text: "Data Store",
                    href: "https://www.propublica.org/datastore/",
                },
            ],
        },
        {
            heading: "Info",
            classSuffix: "info",
            links: [
                { text: "About Us", href: "https://www.propublica.org/about/" },
                { text: "Impact", href: "https://www.propublica.org/impact/" },
                {
                    text: "Jobs & Fellowships",
                    href: "https://www.propublica.org/jobs/",
                },
                {
                    text: "Contact Us",
                    href: "https://www.propublica.org/contact/",
                },
            ],
        },
    ];

    /**
     * @typedef {HeaderLink & { bgColor: string, textColor: string} } FeaturedLink
     */
    /** Featured link. (Shows up on "Full" version of the SiteHeader.)
     *
     * For this prop, use an object with the following fields:
        - `text` (string): "Nonprofit Explorer",
        - `href` (url): "https://projects.propublica.org/nonprofits/",
        - `bgColor` (any valid css color value): "var(--green-50)",
        - `textColor` (any valid css color value): "var(--white)",
     *
     * Set to null to leave empty.
     * @type {FeaturedLink | null}
     */
    export let featuredLink = {
        text: "Nonprofit Explorer",
        href: "https://projects.propublica.org/nonprofits/",
        bgColor: "var(--green-50)",
        textColor: "var(--white)",
    };

    /** @type {HeaderLink} */
    const donateLink = {
        text: "Donate",
        href: donateUrl,
    };

    /** @type {HeaderLink[]} */
    const headerLinks = [
        {
            text: "Local Initiatives",
            href: "https://www.propublica.org/local-initiatives/",
        },
        {
            text: "Newsletters",
            href: "https://www.propublica.org/newsletters/",
        },
        {
            text: "About Us",
            href: "https://www.propublica.org/about/",
        },
    ];
</script>

<a class="site-header-skip-link a11y a11y-focusable--absolute" href="#main"
    >Skip to content</a
>

<svelte:window bind:scrollY on:keydown={handleKeydown} />

{#if type === "minimal"}
    <!-- minimal header (unsticky part)-->
    <div
        class="site-header--minimal hide-print"
        bind:this={scrollStartSiteHeaderEl}
        data-pp-location="svelte-siteheader"
    >
        <div class="variant-minimal__inner-wrap">
            <div class="variant-minimal__section-menu">
                <Button
                    bgColor="transparent"
                    textColor="var(--site-header-color-button-text, var(--color-neutral-80))"
                    iconOnly
                    on:click={openOverlay}
                    dataLink="Menu"
                    dataTestID="minimal-menu-button"
                >
                    <MenuIcon slot="iconBefore" />
                    Menu
                </Button>
            </div>
            <a
                class="variant-minimal__wordmark"
                href="https://www.propublica.org"
            >
                <Logo />
            </a>
            <Button
                elementType="a"
                href={donateLink.href}
                miscAttrs={{
                    "data-pp-view": "",
                }}
                font="serif"
                bgColor="var(--red-50)"
                textColor="var(--white)"
                textSize={breakpoint === "xs" ||
                breakpoint === "sm" ||
                breakpoint === "md"
                    ? "var(--scale-2)"
                    : ""}
                buttonSize={breakpoint === "xs" ||
                breakpoint === "sm" ||
                breakpoint === "md"
                    ? "var(--scale2)"
                    : "var(--scale3)"}
            >
                {donateLink.text}
            </Button>
        </div>
    </div>
{:else if type === "compact"}
    <!-- compact header (at the top, becomes sticky)-->
    <div
        class="site-header--compact"
        class:site-header--compact-has-scrolled={scrollY >=
            fixedNavScrollStartY}
        bind:this={scrollStartSiteHeaderEl}
        data-pp-location="svelte-siteheader"
    >
        <div
            class="variant-compact__inner-wrap"
            class:variant-compact__inner-wrap--has-border={scrollY <
                fixedNavScrollStartY}
        >
            <div class="variant-compact__section-menu">
                <Button
                    bgColor="transparent"
                    textColor={scrollY >= fixedNavScrollStartY
                        ? "var(--site-header-fixed-color-button-text, var(--color-neutral-80))"
                        : "var(--site-header-color-button-text, var(--color-neutral-80))"}
                    iconOnly
                    on:click={openOverlay}
                    dataLink="Menu"
                    dataTestID="compact-menu-button"
                >
                    <MenuIcon slot="iconBefore" />
                    Menu
                </Button>
            </div>
            <a
                class="variant-compact__wordmark"
                href="https://www.propublica.org"
            >
                <Logo />
            </a>
            <Button
                elementType="a"
                href={donateLink.href}
                miscAttrs={{
                    "data-pp-view": "",
                }}
                bgColor="var(--red-50)"
                textColor="var(--white)"
                font="serif"
                textSize={breakpoint === "xs" ||
                breakpoint === "sm" ||
                breakpoint === "md"
                    ? "var(--scale-2)"
                    : ""}
                buttonSize={breakpoint === "xs" ||
                breakpoint === "sm" ||
                breakpoint === "md"
                    ? "var(--scale2)"
                    : "var(--scale3)"}
            >
                {donateLink.text}
            </Button>
        </div>
    </div>
{:else if type === "full"}
    <!-- full header small viewport (unsticky part)-->
    <div
        class="site-header--compact hide-above-lg hide-print"
        class:site-header--compact-has-scrolled={scrollY > fixedNavScrollStartY}
        data-pp-location="svelte-siteheader"
    >
        <div
            class="variant-compact__inner-wrap"
            class:variant-compact__inner-wrap--has-border={scrollY <=
                fixedNavScrollStartY}
        >
            <div class="variant-compact__section-menu">
                <Button
                    bgColor="transparent"
                    textColor="var(--site-header-color-button-text, var(--color-neutral-80))"
                    iconOnly
                    on:click={openOverlay}
                    dataLink="Menu"
                    dataTestID="full-menu-button-on-mobile"
                >
                    <MenuIcon slot="iconBefore" />
                    Menu
                </Button>
            </div>
            <a
                class="variant-compact__wordmark"
                href="https://www.propublica.org"
            >
                <Logo />
            </a>
            <Button
                elementType="a"
                href={donateLink.href}
                miscAttrs={{
                    "data-pp-view": "",
                }}
                bgColor="var(--red-50)"
                textColor="var(--white)"
                font="serif"
                textSize={breakpoint === "xs" ||
                breakpoint === "sm" ||
                breakpoint === "md"
                    ? "var(--scale-2)"
                    : ""}
                buttonSize={breakpoint === "xs" ||
                breakpoint === "sm" ||
                breakpoint === "md"
                    ? "var(--scale2)"
                    : "var(--scale3)"}
            >
                {donateLink.text}
            </Button>
        </div>
    </div>
    <!-- full header large viewport (unsticky part) -->
    <div
        class="site-header--full hide-below-lg hide-print"
        bind:this={scrollStartSiteHeaderEl}
        data-pp-location="svelte-siteheader"
    >
        <div class="site-header--full__inner-wrap">
            <!-- This wrapper div is needed to have the background color stretch across the full viewport width, while setting the width of its contents to a smaller width via the grid mixin. -->
            <div class="section-logo">
                <a class="wordmark" href="https://www.propublica.org">
                    <Logo />
                </a>

                <p class="tagline">
                    <strong
                        >Investigative Journalism in the Public Interest</strong
                    >
                </p>
            </div>

            <div class="section-menu">
                <Button
                    bgColor="var(--site-header-color-button-bg, var(--color-neutral-10))"
                    textColor="var(--site-header-color-button-text, var(--color-neutral-80))"
                    on:click={openOverlay}
                    dataLink="Menu"
                    dataTestID="full-menu-button-on-desktop"
                >
                    <MenuIcon slot="iconBefore" />
                    Menu
                </Button>

                <Button
                    bgColor="var(--site-header-color-button-bg, var(--color-neutral-10))"
                    textColor="var(--site-header-color-button-text, var(--color-neutral-80))"
                    iconOnly={true}
                    shape="circle"
                    on:click={() => {
                        isOverlayOpen = true;
                        overlaySearchInputEl.focus();
                    }}
                    dataLink="Search"
                    dataTestID="search-button"
                >
                    <SearchIcon slot="iconBefore" />
                    Search
                </Button>
            </div>

            <div class="section-donate">
                <Button
                    elementType="a"
                    href={donateLink.href}
                    miscAttrs={{
                        "data-pp-view": "",
                    }}
                    bgColor="var(--red-50)"
                    textColor="var(--white)"
                    font="serif"
                    textSize={breakpoint === "xs" ||
                    breakpoint === "sm" ||
                    breakpoint === "md"
                        ? "var(--scale-2)"
                        : ""}
                    horizontalSpacing="var(--scale3)"
                >
                    {donateLink.text}
                </Button>
            </div>

            <hr class="site-header--full__divider" />

            <div class="section-links">
                <ul class="links-list">
                    {#if featuredLink}
                        <li>
                            <a
                                class="link link--featured"
                                href={featuredLink.href}
                                style:--site-header__featured-link-bg-color={featuredLink.bgColor}
                                style:--site-header__featured-link-text-color={featuredLink.textColor}
                                >{featuredLink.text}</a
                            >
                        </li>
                    {/if}
                    {#each headerLinks as link}
                        <li>
                            <a class="link" href={link.href}>
                                {link.text}
                            </a>
                        </li>
                    {/each}
                </ul>
            </div>

            <div class="section-newsletter">
                <NewsletterSignup
                    {...newsletterSignupProps}
                    newsletterList="The Big Story"
                    placeholderText="Get the Big Story newsletter"
                    inputID="site-header-signup"
                    placement="main-nav"
                />
            </div>
        </div>
    </div>
{/if}
{#if type === "minimal" || type === "full"}
    <!-- sticky header for minimal & full -->
    <div
        class="site-header--fixed"
        class:hide-below-lg={type === "full"}
        class:site-header--fixed-has-scrolled={scrollY > fixedNavScrollStartY}
        aria-hidden="true"
        data-pp-location="svelte-siteheader"
    >
        <div class="variant-fixed__inner-wrap">
            <div class="variant-fixed__section-menu">
                <Button
                    bgColor="transparent"
                    textColor="var(--site-header-fixed-color-button-text, var(--color-neutral-80))"
                    iconOnly
                    on:click={openOverlay}
                    dataLink="Menu"
                    dataTestID="minimal-or-full-menu-button-in-fixed-nav"
                >
                    <MenuIcon slot="iconBefore" />
                    Menu
                </Button>
            </div>
            <a
                class="variant-fixed__wordmark"
                href="https://www.propublica.org"
            >
                <Logo />
            </a>
            <!-- The following donate button purposefully lacks data-pp-view
                since we figured it's more useful for calcualations to just
                track the view of the donate button on the top, non-sticky
                header for full & minimal. But track clicks on either non-sticky
                or sticky header. (All get associated with same
                data-pp-location)-->
            <Button
                elementType="a"
                href={donateLink.href}
                bgColor="var(--red-50)"
                textColor="var(--white)"
                font="serif"
                textSize={breakpoint === "xs" ||
                breakpoint === "sm" ||
                breakpoint === "md"
                    ? "var(--scale-2)"
                    : "var(--scale-1"}
                buttonSize={breakpoint === "xs" ||
                breakpoint === "sm" ||
                breakpoint === "md"
                    ? "var(--scale2)"
                    : "var(--scale3)"}
            >
                {donateLink.text}
            </Button>
        </div>
    </div>
{/if}

<div
    class="site-header-overlay"
    style:opacity={isOverlayOpen ? "1" : "0"}
    style:pointer-events={isOverlayOpen ? "auto" : "none"}
    data-testid="overlay"
    data-pp-location="svelte-siteheader-overlay"
>
    <div class="variant-overlay__top-inner-wrap">
        <div class="variant-overlay__section-menu">
            <Button
                bgColor="transparent"
                iconSize="var(--scale0)"
                textColor="white"
                iconOnly
                on:click={closeOverlay}
                dataLink="Menu"
                dataTestID="close-overlay-button"
            >
                <CloseIcon slot="iconBefore" />
                Close
            </Button>
        </div>
        <a class="variant-overlay__wordmark" href="https://www.propublica.org">
            <Logo />
        </a>
        <Button
            elementType="a"
            href={donateLink.href}
            miscAttrs={{
                "data-pp-view": "",
            }}
            bgColor="var(--red-50)"
            textColor="var(--white)"
            font="serif"
            textSize={breakpoint === "xs" ||
            breakpoint === "sm" ||
            breakpoint === "md"
                ? "var(--scale-2)"
                : ""}
            buttonSize={breakpoint === "xs" ||
            breakpoint === "sm" ||
            breakpoint === "md"
                ? "var(--scale2)"
                : "var(--scale3)"}
        >
            {donateLink.text}
        </Button>
    </div>

    <div
        class="site-header-overlay__wrap-expanded"
        data-pp-location="masthead wrap-expanded"
    >
        <!-- Search -->

        <div class="site-header__section-search">
            <form
                class="search-form"
                role="search"
                action="https://propublica.org/search"
                method="get"
            >
                <label
                    class="a11y hed-form hed-site-search"
                    for="search-form__input-text">Search ProPublica:</label
                >
                <div class="search-form__input-wrap">
                    <input
                        bind:this={overlaySearchInputEl}
                        placeholder="Search"
                        id="search-form__input-text"
                        class="search-form__input-text"
                        type="search"
                        value=""
                        name="qss"
                    />
                    <Button
                        bgColor="var(--cool-70)"
                        buttonSize="var(--scale4)"
                        iconOnly={true}
                        iconSize="var(--scale0)"
                        shape="circle"
                        textColor="white"
                    >
                        <SearchIcon slot="iconBefore" />
                        Search
                    </Button>
                </div>
            </form>
        </div>
        <!-- end .site-header__section-search -->

        <!-- Links expanded -->

        <nav
            class="site-header__section-links-expanded"
            aria-label="Expanded Links"
            hidden
        >
            {#each overlayLinkSections as section}
                <div
                    class="site-header__links site-header__links-{section.classSuffix}"
                >
                    {#if section.heading}
                        <strong class="site-header__links-heading">
                            {section.heading}
                        </strong>
                    {/if}
                    <ul class="site-header__links-list">
                        {#each section.links as link}
                            <li>
                                {#if link.icon}
                                    <span class="icon">
                                        <svelte:component this={link.icon} />
                                    </span>
                                {/if}
                                <a
                                    class="site-header__link"
                                    data-link={link.text}
                                    href={link.href}
                                >
                                    {link.text}
                                </a>
                            </li>
                        {/each}
                    </ul>
                </div>
            {/each}

            <!-- Follow -->

            <div class="site-header__links site-header__links-follow">
                <strong class="site-header__links-heading">Follow</strong>
                <ul
                    class="site-header__links-list site-header__links-list-follow"
                    data-link="Facebook"
                >
                    <li>
                        <Button
                            elementType="a"
                            href="https://www.facebook.com/propublica"
                            iconOnly={true}
                            textColor="white"
                            shape="circle"
                            bgColor="var(--cool-70)"
                        >
                            <FacebookIcon slot="iconBefore" />
                            Facebook
                        </Button>
                    </li>
                    <li data-link="Twitter">
                        <Button
                            elementType="a"
                            href="https://twitter.com/propublica"
                            iconOnly={true}
                            textColor="white"
                            shape="circle"
                            bgColor="var(--cool-70)"
                        >
                            <TwitterIcon slot="iconBefore" />
                            Twitter
                        </Button>
                    </li>
                    <li data-link="Instagram">
                        <Button
                            elementType="a"
                            href="https://www.instagram.com/propublica/"
                            iconOnly={true}
                            textColor="white"
                            shape="circle"
                            bgColor="var(--cool-70)"
                        >
                            <InstagramIcon slot="iconBefore" />
                            Instagram
                        </Button>
                    </li>
                </ul>
            </div>
            <!-- end .site-header__links-follow -->
        </nav>
        <!-- end .site-header__section-links-expanded -->
    </div>
    <!-- end .site-header-overlay__wrap-expanded -->
</div>

<style>.site-header--minimal,
.site-header--compact,
.site-header--full,
.site-header--fixed,
.site-header-overlay {
  --header-hover: var(--color-accent-50);
  color: var(--site-header-color-text, var(--color-neutral-80));
  font-family: var(--fonts-sans);
  line-height: var(--line-height-2);
}

.site-header--compact,
.site-header--full {
  background-color: var(--site-header-color-bg, var(--color-bg));
}

a {
  color: currentColor;
}

.site-header-skip-link {
  background-color: var(--color-neutral-10);
  border-radius: 4px;
  display: block;
  font-family: var(--fonts-sans);
  font-size: var(--scale-2);
  font-weight: 700;
  line-height: var(--line-height-2);
  padding: var(--spacing-1);
  text-decoration: none;
  left: 1rem;
  top: 1rem;
  z-index: 1001;
}
.site-header-skip-link:focus {
  outline-offset: 4px;
  border: none;
}

.site-header--minimal,
.site-header--compact,
.site-header--fixed {
  width: 100%;
}

.site-header--minimal {
  --site-header-page-margins: var(
      --spacing1
  );
  position: absolute;
  z-index: 1000;
}
@media screen and (min-width: 48em) {
  .site-header--minimal {
    --site-header-page-margins: var(--spacing3);
  }
}

.site-header--compact,
.site-header--fixed {
  position: sticky;
  top: 0;
  z-index: 1000;
}
@media print {
  .site-header--compact,
  .site-header--fixed {
    margin-bottom: 15pt;
    position: static !important;
    transform: translateY(0) !important;
  }
}

@media print {
  .site-header--compact .variant-compact__inner-wrap--has-border,
  .site-header--fixed {
    border-bottom: none !important;
  }
}

.site-header--compact-has-scrolled,
.site-header--fixed {
  backdrop-filter: blur(20px);
  background-color: var(--site-header-fixed-color-bg, var(--color-bg));
  background-color: color-mix(in oklab, var(--site-header-fixed-color-bg, var(--color-bg)) 90%, transparent);
  border-bottom: 1px solid var(--site-header-fixed-color-border, var(--color-neutral-10));
  color: var(--site-header-fixed-color-text, --color-neutral-80);
}

.site-header--fixed {
  position: fixed;
  top: 0;
  transform: translateY(-150%);
  transition: 0.2s;
}

.site-header--fixed-has-scrolled {
  transform: translateY(0%);
}

.variant-minimal__inner-wrap,
.variant-compact__inner-wrap,
.variant-fixed__inner-wrap,
.variant-overlay__top-inner-wrap {
  align-items: center;
  display: grid;
  font-size: var(--scale-2);
  grid-template-areas: "menu logo donate";
  padding: var(--spacing-1) 0;
}
@media screen and (min-width: 36em) {
  .variant-minimal__inner-wrap,
  .variant-compact__inner-wrap,
  .variant-fixed__inner-wrap,
  .variant-overlay__top-inner-wrap {
    font-size: var(--scale-1);
  }
}

/*
For the compact site-header, we apply the bottom border in two different places based on scroll position:
- Before someone has scrolled, it’s applied on an inner div so that the border spans the nav contents.
- After someone has scrolled, it’s applied on the outer div so that the border spans the full browser viewport.
*/
.variant-compact__inner-wrap--has-border {
  border-bottom: 1px solid var(--site-header-color-border, var(--color-neutral-10));
}

.variant-minimal__inner-wrap {
  grid-template-columns: 5em 1fr 5em;
  margin: 0 var(--site-header-page-margins);
}
@media screen and (min-width: 48em) {
  .variant-minimal__inner-wrap {
    --site-header-page-margins: var(--spacing3);
  }
}
@media screen and (min-width: 60em) {
  .variant-minimal__inner-wrap {
    grid-template-columns: 2rem 1fr 5em;
  }
}

.variant-compact__inner-wrap {
  max-width: 1344px;
}
@media screen and (min-width: 0) {
  .variant-compact__inner-wrap {
    margin: 0 1.44rem;
  }
}
@media screen and (min-width: 36em) {
  .variant-compact__inner-wrap {
    margin: 0 1.44rem;
  }
}
@media screen and (min-width: 48em) {
  .variant-compact__inner-wrap {
    margin: 0 2.985984rem;
  }
}
@media screen and (min-width: 60em) {
  .variant-compact__inner-wrap {
    margin: 0 2.985984rem;
  }
}
@media screen and (min-width: 78em) {
  .variant-compact__inner-wrap {
    margin: 0 2.985984rem;
  }
}
@media screen and (min-width: 88em) {
  .variant-compact__inner-wrap {
    margin: 0 2.985984rem;
  }
}
@media screen and (min-width: 1439.551488px) {
  .variant-compact__inner-wrap {
    margin: 0 auto;
  }
}

.variant-fixed__inner-wrap {
  max-width: 1344px;
}
@media screen and (min-width: 0) {
  .variant-fixed__inner-wrap {
    margin: 0 1.44rem;
  }
}
@media screen and (min-width: 36em) {
  .variant-fixed__inner-wrap {
    margin: 0 1.44rem;
  }
}
@media screen and (min-width: 48em) {
  .variant-fixed__inner-wrap {
    margin: 0 2.985984rem;
  }
}
@media screen and (min-width: 60em) {
  .variant-fixed__inner-wrap {
    margin: 0 2.985984rem;
  }
}
@media screen and (min-width: 78em) {
  .variant-fixed__inner-wrap {
    margin: 0 2.985984rem;
  }
}
@media screen and (min-width: 88em) {
  .variant-fixed__inner-wrap {
    margin: 0 2.985984rem;
  }
}
@media screen and (min-width: 1439.551488px) {
  .variant-fixed__inner-wrap {
    margin: 0 auto;
  }
}

.variant-overlay__top-inner-wrap {
  max-width: 1344px;
}
@media screen and (min-width: 0) {
  .variant-overlay__top-inner-wrap {
    margin: 0 1.44rem;
  }
}
@media screen and (min-width: 36em) {
  .variant-overlay__top-inner-wrap {
    margin: 0 1.44rem;
  }
}
@media screen and (min-width: 48em) {
  .variant-overlay__top-inner-wrap {
    margin: 0 2.985984rem;
  }
}
@media screen and (min-width: 60em) {
  .variant-overlay__top-inner-wrap {
    margin: 0 2.985984rem;
  }
}
@media screen and (min-width: 78em) {
  .variant-overlay__top-inner-wrap {
    margin: 0 2.985984rem;
  }
}
@media screen and (min-width: 88em) {
  .variant-overlay__top-inner-wrap {
    margin: 0 2.985984rem;
  }
}
@media screen and (min-width: 1439.551488px) {
  .variant-overlay__top-inner-wrap {
    margin: 0 auto;
  }
}

.variant-compact__inner-wrap,
.variant-fixed__inner-wrap,
.variant-overlay__top-inner-wrap {
  grid-template-columns: 5em 1fr 5em;
}

.variant-minimal__section-menu,
.variant-compact__section-menu,
.variant-fixed__section-menu,
.variant-overlay__section-menu {
  grid-area: menu;
  margin-left: -0.75rem;
}

.variant-minimal__wordmark,
.variant-compact__wordmark,
.variant-fixed__wordmark,
.variant-overlay__wordmark {
  display: block;
  fill: currentColor;
  grid-area: logo;
  justify-self: center;
  margin: 0 var(--spacing-1);
  max-width: 13em;
  width: 100%;
}

.variant-minimal__wordmark {
  justify-self: center;
}
@media screen and (min-width: 60em) {
  .variant-minimal__wordmark {
    justify-self: start;
  }
}

.site-header--full__inner-wrap {
  max-width: 1344px;
}
@media screen and (min-width: 0) {
  .site-header--full__inner-wrap {
    margin: 0 1.44rem;
  }
}
@media screen and (min-width: 36em) {
  .site-header--full__inner-wrap {
    margin: 0 1.44rem;
  }
}
@media screen and (min-width: 48em) {
  .site-header--full__inner-wrap {
    margin: 0 2.985984rem;
  }
}
@media screen and (min-width: 60em) {
  .site-header--full__inner-wrap {
    margin: 0 2.985984rem;
  }
}
@media screen and (min-width: 78em) {
  .site-header--full__inner-wrap {
    margin: 0 2.985984rem;
  }
}
@media screen and (min-width: 88em) {
  .site-header--full__inner-wrap {
    margin: 0 2.985984rem;
  }
}
@media screen and (min-width: 1439.551488px) {
  .site-header--full__inner-wrap {
    margin: 0 auto;
  }
}

.site-header--full__inner-wrap {
  align-items: center;
  border: none;
  display: grid;
  font-size: var(--scale-1);
  grid-template-areas: "menu   logo   logo       donate" "border border border     border" "links  links  newsletter newsletter";
  grid-template-columns: 13em 1fr 7em 13em;
  padding: var(--spacing2) 0;
  row-gap: var(--spacing-1);
}

.section-menu {
  grid-area: menu;
  padding-bottom: var(--spacing0);
  justify-self: start;
  display: flex;
  flex-direction: row;
  gap: var(--spacing0);
}

.site-header--full .section-logo {
  align-items: center;
  display: flex;
  flex-direction: column;
  grid-area: logo;
  justify-content: center;
  justify-self: stretch;
  padding: 0 0 var(--spacing0);
}

.site-header--full .wordmark {
  display: block;
  max-width: 25em;
  width: 100%;
}
.site-header--full .wordmark:hover {
  color: var(--header-hover);
}

.section-donate {
  grid-area: donate;
  justify-self: end;
  padding-bottom: var(--spacing0);
}

.site-header--full__divider {
  background-color: var(--site-header-color-border, var(--color-neutral-10));
  border: none;
  grid-area: border;
  height: 1px;
  margin: 0;
  width: 100%;
}

.section-links {
  grid-area: links;
}

.links-list {
  align-items: center;
  display: flex;
  flex-direction: row;
  gap: var(--spacing0);
}
@media screen and (min-width: 78em) {
  .links-list {
    gap: var(--spacing2);
  }
}

.link {
  text-decoration: none;
  font-size: var(--scale-2);
  font-weight: 700;
  transition: 0.2s;
}
.link:hover {
  color: var(--color-accent-50);
}

.link--featured {
  color: var(--site-header__featured-link-text-color);
  background-color: var(--site-header__featured-link-bg-color);
  padding: 0.3em 0.4em;
}

.link--featured:hover {
  color: var(--site-header__featured-link-text-color);
  background-color: var(--site-header__featured-link-bg-color);
  opacity: 0.75;
}

.section-newsletter {
  border-left: 1px solid var(--site-header-color-border, var(--color-neutral-10));
  grid-area: newsletter;
  justify-self: stretch;
  padding-left: var(--spacing0);
}

.tagline {
  display: block;
  font-family: var(--fonts-serif);
  font-size: var(--scale-2);
  font-weight: 700;
  margin-top: var(--spacing-2);
  text-align: center;
}

.site-header-overlay {
  background-color: var(--cool-90);
  color: var(--cool-10);
  bottom: 0;
  left: 0;
  overflow: scroll;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 1001;
  transition: opacity 0.2s;
}

.site-header-overlay__wrap-expanded {
  max-width: 1344px;
}
@media screen and (min-width: 0) {
  .site-header-overlay__wrap-expanded {
    margin: 0 1.44rem;
  }
}
@media screen and (min-width: 36em) {
  .site-header-overlay__wrap-expanded {
    margin: 0 1.44rem;
  }
}
@media screen and (min-width: 48em) {
  .site-header-overlay__wrap-expanded {
    margin: 0 2.985984rem;
  }
}
@media screen and (min-width: 60em) {
  .site-header-overlay__wrap-expanded {
    margin: 0 2.985984rem;
  }
}
@media screen and (min-width: 78em) {
  .site-header-overlay__wrap-expanded {
    margin: 0 2.985984rem;
  }
}
@media screen and (min-width: 88em) {
  .site-header-overlay__wrap-expanded {
    margin: 0 2.985984rem;
  }
}
@media screen and (min-width: 1439.551488px) {
  .site-header-overlay__wrap-expanded {
    margin: 0 auto;
  }
}

.site-header-overlay__wrap-expanded {
  align-items: center;
  display: flex;
  flex-direction: column;
  padding-bottom: var(--spacing2);
}
@media screen and (min-width: 48em) {
  .site-header-overlay__wrap-expanded {
    padding-bottom: var(--spacing3);
  }
}
@media screen and (min-width: 60em) {
  .site-header-overlay__wrap-expanded {
    margin-top: 5rem;
  }
}

.site-header__section-search {
  width: 100%;
}

.search-form {
  font-size: var(--scale0);
  padding: var(--spacing1) 0 var(--spacing3);
}
@media screen and (min-width: 48em) {
  .search-form {
    padding-top: 0;
  }
}

.search-form__input-wrap {
  align-items: center;
  display: flex;
  justify-content: center;
}

.search-form__input-text {
  background-color: var(--cool-80);
  border: none;
  border-radius: 0.2em;
  color: var(--cool-10);
  font-family: var(--fonts-sans);
  font-size: inherit;
  padding: 0.5em;
  margin-right: var(--spacing-1);
  max-width: 20em;
  width: 100%;
}
.search-form__input-text::placeholder {
  color: inherit;
}

.site-header__section-links-expanded {
  column-gap: var(--spacing0);
  display: grid;
  grid-template-areas: "special border1 region" "topics border1 type" "info border1 follow";
  grid-template-columns: 1fr 1px 1fr;
  row-gap: var(--spacing1);
}
@media screen and (min-width: 48em) {
  .site-header__section-links-expanded {
    column-gap: var(--spacing1);
    grid-template-areas: "special border1 region border2 info" "topics border1 type border2 follow";
    grid-template-columns: 1fr 1px 1fr 1px 1fr;
  }
}
.site-header__section-links-expanded::before, .site-header__section-links-expanded::after {
  background-color: var(--cool-70);
  display: block;
}
.site-header__section-links-expanded::before {
  content: "";
  grid-area: border1;
}
@media screen and (min-width: 48em) {
  .site-header__section-links-expanded::after {
    content: "";
    grid-area: border2;
  }
}

.site-header__links-heading {
  display: block;
  font-size: var(--scale0);
  font-weight: bold;
  margin-bottom: var(--spacing1);
}

.site-header__links-list {
  display: block;
  font-size: var(--scale-1);
}
.site-header__links-list > * {
  margin-bottom: var(--spacing0);
}
.site-header__links-list > *:last-child {
  margin-bottom: 0;
}

.site-header__link {
  text-decoration: none;
}
.site-header__link:focus, .site-header__link:hover {
  color: var(--header-hover);
}

.site-header__links-topics::before,
.site-header__links-type::before,
.site-header__links-info::before,
.site-header__links-follow::before {
  background-color: var(--cool-70);
  content: "";
  display: block;
  height: 1px;
  margin-bottom: var(--spacing1);
  width: 50%;
}

@media screen and (min-width: 48em) {
  .site-header__links-info::before {
    display: none;
  }
}

.site-header__links-topics {
  grid-area: topics;
}
.site-header__links-topics .icon {
  margin-right: var(--spacing-2);
  vertical-align: -0.1em;
}

.site-header__links-region {
  grid-area: region;
}

.site-header__links-type {
  grid-area: type;
}

.site-header__links-special {
  font-weight: bold;
  grid-area: special;
}
.site-header__links-special .site-header__link {
  font-size: var(--scale0);
}

.site-header__links-info {
  grid-area: info;
  order: -1;
}

.site-header__links-follow {
  grid-area: follow;
}

.site-header__links-list-follow {
  display: flex;
  flex-wrap: wrap;
  font-size: var(--scale0);
}
.site-header__links-list-follow > * {
  margin-right: var(--spacing-1);
  margin-top: 0;
}
.site-header__links-list-follow > *:last-child {
  margin-right: 0;
}</style>
